/**
 * ALT Enterprise Analytics Platform - Main App
 * Unified multi-domain dashboard with routing
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BusinessEntityProvider } from './contexts/BusinessEntityContext';
import { BranchProvider } from './contexts/BranchContext';
import ProtectedRoute from './components/ProtectedRoute';
import DomainProtectedRoute from './components/DomainProtectedRoute';
import RouteNotFound from './components/RouteNotFound';
import DomainIndexRedirect from './components/DomainIndexRedirect';
import MainLayout from './layouts/MainLayout';

// Marketing layout and pages - lazy load for code splitting
const MarketingLayout = React.lazy(() => import('./domains/marketing/layouts/MarketingLayout'));
const OverviewPage = React.lazy(() => import('./domains/marketing/pages/OverviewPage'));
const MarketingPerformancePage = React.lazy(() => import('./domains/marketing/pages/MarketingPerformancePage'));
const LeadPipelinePage = React.lazy(() => import('./domains/marketing/pages/LeadPipelinePage'));
const CampaignsPage = React.lazy(() => import('./domains/marketing/pages/CampaignsPage'));
const WebTrafficPage = React.lazy(() => import('./domains/marketing/pages/WebTrafficPage'));
const MarketPage = React.lazy(() => import('./domains/marketing/pages/MarketPage'));
const CompetitorsPage = React.lazy(() => import('./domains/marketing/pages/CompetitorsPage'));
const TrendsPage = React.lazy(() => import('./domains/marketing/pages/TrendsPage'));
const PartnershipsPage = React.lazy(() => import('./domains/marketing/pages/PartnershipsPage'));
const MarketingDataModel = React.lazy(() => import('./domains/marketing/pages/DataModelPage'));
const MarketingDataManagementPage = React.lazy(() => import('./domains/marketing/pages/DataManagementPage'));
const OnlineReviewsPage = React.lazy(() => import('./domains/marketing/pages/OnlineReviewsPage'));
const AdvertisingPerformancePage = React.lazy(() => import('./domains/marketing/pages/AdvertisingPerformancePage'));
const MarketSharePage = React.lazy(() => import('./domains/marketing/pages/MarketSharePage'));
const CompletionGuaranteesPage = React.lazy(() => import('./domains/marketing/pages/CompletionGuaranteesPage'));
const MarketingDocumentationPage = React.lazy(() => import('./domains/marketing/pages/DocumentationPage'));

// Finance layout and pages - lazy load
const FinanceLayout = React.lazy(() => import('./domains/finance/layouts/FinanceLayout'));
const FinanceOverview = React.lazy(() => import('./domains/finance/pages/FinanceOverview'));
const ProfitabilityPage = React.lazy(() => import('./domains/finance/pages/ProfitabilityPage'));
const SegmentPLPage = React.lazy(() => import('./domains/finance/pages/SegmentPLPage'));
const VelixoUploadPage = React.lazy(() => import('./domains/finance/pages/VelixoUploadPage'));
const BalanceSheetPage = React.lazy(() => import('./domains/finance/pages/BalanceSheetPage'));
const CashFlowPage = React.lazy(() => import('./domains/finance/pages/CashFlowPage'));
const WorkingCapitalPage = React.lazy(() => import('./domains/finance/pages/WorkingCapitalPage'));
const ProjectCostingPage = React.lazy(() => import('./domains/finance/pages/ProjectCostingPage'));
const DataModelPage = React.lazy(() => import('./domains/finance/pages/DataModelPage'));
const FinanceDataManagementPage = React.lazy(() => import('./domains/finance/pages/DataManagementPage'));
const TrialBalancePage = React.lazy(() => import('./domains/finance/pages/TrialBalancePage'));
// Finance Dashboard Architecture (Matt spec 2026-06-30)
const FinanceManagementOverlay  = React.lazy(() => import('./domains/finance/pages/FinanceManagementOverlay'));
const FinanceCovenants          = React.lazy(() => import('./domains/finance/pages/FinanceCovenants'));
const FinanceArchitectureConfig = React.lazy(() => import('./domains/finance/pages/FinanceArchitectureConfig'));

// Sales layout and pages - lazy load
const SalesLayout = React.lazy(() => import('./domains/sales/layouts/SalesLayout'));
const SalesDashboard = React.lazy(() => import('./domains/sales/pages/SalesDashboard'));
const SalesFactBrowserPage = React.lazy(() => import('./domains/sales/pages/FactTableBrowserPage'));
const SalesDataModelPage = React.lazy(() => import('./domains/sales/pages/DataModelPage'));
const SalesDataManagementPage = React.lazy(() => import('./domains/sales/pages/DataManagementPage'));

// Manufacturing pages - lazy load
const ManufacturingDashboard = React.lazy(() => import('./domains/manufacturing/pages/ManufacturingDashboard'));

// Operations pages - lazy load
const OperationsDashboard = React.lazy(() => import('./domains/operations/pages/OperationsDashboard'));
const Project360Layout = React.lazy(() => import('./domains/operations/layouts/Project360Layout'));
const Project360Page = React.lazy(() => import('./domains/operations/pages/Project360Page'));
const Project360ComparePage = React.lazy(() => import('./domains/operations/pages/Project360ComparePage'));
const Project360TrendsPage = React.lazy(() => import('./domains/operations/pages/Project360TrendsPage'));
const Project360PhaseCostingPage = React.lazy(() => import('./domains/operations/pages/Project360PhaseCostingPage'));
const ProjectionsPage = React.lazy(() => import('./domains/operations/pages/ProjectionsPage'));

// Logistics layout and pages - lazy load
const LogisticsLayout = React.lazy(() => import('./domains/logistics/layouts/LogisticsLayout'));
const DealerMapPage = React.lazy(() => import('./domains/logistics/pages/DealerMapPage'));
const DealerPipelinePage = React.lazy(() => import('./domains/logistics/pages/DealerPipelinePage'));
const LogisticsDataManagementPage = React.lazy(() => import('./domains/logistics/pages/DataManagementPage'));
const LogisticsMappingDataPage    = React.lazy(() => import('./domains/logistics/pages/MappingDataPage'));
const ResidentialConsultantsPage  = React.lazy(() => import('./domains/logistics/pages/ResidentialConsultantsPage'));

// Procurement & Inventory layout and pages - lazy load
const ProcurementLayout = React.lazy(() => import('./domains/procurement/layouts/ProcurementLayout'));
const ProcurementInventoryPage = React.lazy(() => import('./domains/procurement/pages/InventoryPage'));
const ProcurementPurchaseOrdersPage = React.lazy(() => import('./domains/procurement/pages/PurchaseOrdersPage'));
const ProcurementProductionOrdersPage = React.lazy(() => import('./domains/procurement/pages/ProductionOrdersPage'));
const ProcurementReportsPage = React.lazy(() => import('./domains/procurement/pages/ReportsPage'));
const ProcurementEquipmentPage = React.lazy(() => import('./domains/procurement/pages/EquipmentPage'));
const ProcurementDataManagementPage = React.lazy(() => import('./domains/procurement/pages/DataManagementPage'));

// DTO (Digital Transformation Office) - lazy load
const DtoLayout         = React.lazy(() => import('./domains/dto/layouts/DtoLayout'));
const DtoOverview       = React.lazy(() => import('./domains/dto/pages/DtoOverview'));
const DtoPipeline       = React.lazy(() => import('./domains/dto/pages/DtoPipeline'));
const DtoMonthly        = React.lazy(() => import('./domains/dto/pages/DtoMonthly'));
const DtoCategories     = React.lazy(() => import('./domains/dto/pages/DtoCategories'));
const DtoDataManagement = React.lazy(() => import('./domains/dto/pages/DtoDataManagement'));

// Management Pack - lazy load
const MgmtPackLayout       = React.lazy(() => import('./domains/mgmt-pack/layouts/MgmtPackLayout'));
const MgmtPackWidgetAdmin  = React.lazy(() => import('./domains/mgmt-pack/pages/MgmtPackWidgetAdmin'));

// Admin panel layout and page - lazy load
const AdminLayout = React.lazy(() => import('./pages/AdminLayout'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const ScreensAdmin = React.lazy(() => import('./pages/ScreensAdmin'));
const AdminHome = React.lazy(() => import('./pages/admin/AdminHome'));
const AdminDataManagement = React.lazy(() => import('./pages/admin/AdminDataManagement'));
const DatabaseMigrations = React.lazy(() => import('./pages/admin/DatabaseMigrations'));
const DomainConfigPage = React.lazy(() => import('./pages/DomainConfigPage'));

// Widget Validator
const WidgetValidator = React.lazy(() => import('./pages/WidgetValidator'));

// Login page (new auth system)
const Login = React.lazy(() => import('./pages/Login'));

// Landing page
const LandingPage = React.lazy(() => import('./pages/LandingPage'));


// /admin-panel/roles -> /admin/roles etc.
function AdminPanelRedirect() {
  const { section } = useParams();
  return <Navigate to={`/admin/${section}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessEntityProvider>
        <BranchProvider>
        <React.Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Login - outside MainLayout */}
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            {/* Default route - Landing Page */}
            <Route index element={<LandingPage />} />


            {/* Marketing Domain */}
            <Route path="marketing" element={<DomainProtectedRoute domain="marketing"><MarketingLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="marketing" fallbackPath="/marketing/overview" />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="performance" element={<MarketingPerformancePage />} />
              <Route path="leads-pipeline" element={<LeadPipelinePage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="web-traffic" element={<WebTrafficPage />} />
              <Route path="market" element={<MarketPage />} />
              <Route path="competitors" element={<CompetitorsPage />} />
              <Route path="trends" element={<TrendsPage />} />
              <Route path="partnerships" element={<PartnershipsPage />} />
              <Route path="online-reviews" element={<OnlineReviewsPage />} />
              <Route path="advertising-performance" element={<AdvertisingPerformancePage />} />
              <Route path="market-share" element={<MarketSharePage />} />
              <Route path="completion-guarantees" element={<CompletionGuaranteesPage />} />
              <Route path="data-model" element={<MarketingDataModel />} />
              <Route path="data-management" element={<MarketingDataManagementPage />} />
              {/* Upload Management removed 2026-10-25 — marketing data will come from CLaMP. Old links land on Data Management. */}
              <Route path="upload-management" element={<Navigate to="/marketing/data-management" replace />} />
              <Route path="documentation" element={<MarketingDocumentationPage />} />
            </Route>

            {/* Finance Domain */}
            <Route path="finance" element={<DomainProtectedRoute domain="finance"><FinanceLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="finance" fallbackPath="/finance/overview" />} />
              <Route path="overview" element={<FinanceOverview />} />
              <Route path="profitability" element={<ProfitabilityPage />} />
              <Route path="segment-pl" element={<SegmentPLPage />} />
              <Route path="balance-sheet" element={<BalanceSheetPage />} />
              <Route path="cash-flow" element={<CashFlowPage />} />
              <Route path="working-capital" element={<WorkingCapitalPage />} />
              <Route path="project-costing" element={<ProjectCostingPage />} />
              <Route path="trial-balance" element={<TrialBalancePage />} />
              <Route path="data-model" element={<DataModelPage />} />
              <Route path="data-management" element={<FinanceDataManagementPage />} />
              <Route path="velixo-mapping" element={<VelixoUploadPage />} />
              {/* Finance Dashboard Architecture (Matt 2026-06-30) */}
              <Route path="management-overlay"   element={<FinanceManagementOverlay />} />
              <Route path="covenants"            element={<FinanceCovenants />} />
              <Route path="architecture-config"  element={<FinanceArchitectureConfig />} />
            </Route>

            {/* Sales Domain */}
            <Route path="sales" element={<DomainProtectedRoute domain="sales"><SalesLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="sales" fallbackPath="/sales/dashboard" />} />
              <Route path="dashboard" element={<SalesDashboard />} />
              <Route path="fact-browser" element={<SalesFactBrowserPage />} />
              <Route path="data-model" element={<SalesDataModelPage />} />
              <Route path="data-management" element={<SalesDataManagementPage />} />
            </Route>

            {/* Manufacturing Domain */}
            <Route path="manufacturing" element={<DomainProtectedRoute domain="manufacturing"><ManufacturingDashboard /></DomainProtectedRoute>} />
            <Route path="manufacturing/:tab" element={<DomainProtectedRoute domain="manufacturing"><ManufacturingDashboard /></DomainProtectedRoute>} />

            {/* Operations Domain */}
            <Route path="operations" element={<DomainProtectedRoute domain="operations"><OperationsDashboard /></DomainProtectedRoute>} />
            <Route path="operations/project-360" element={<DomainProtectedRoute domain="operations"><Project360Layout /></DomainProtectedRoute>}>
              <Route index element={<Project360Page />} />
              <Route path="compare" element={<Project360ComparePage />} />
              <Route path="trends" element={<Project360TrendsPage />} />
              <Route path="phase-costing" element={<Project360PhaseCostingPage />} />
            </Route>
            <Route path="operations/projections" element={<DomainProtectedRoute domain="operations"><ProjectionsPage /></DomainProtectedRoute>} />
            <Route path="operations/:tab" element={<DomainProtectedRoute domain="operations"><OperationsDashboard /></DomainProtectedRoute>} />

            {/* Logistics Domain */}
            <Route path="logistics" element={<DomainProtectedRoute domain="logistics"><LogisticsLayout /></DomainProtectedRoute>}>
              <Route index element={<Navigate to="dealer-map" replace />} />
              <Route path="dealer-map" element={<DealerMapPage />} />
              <Route path="pipeline" element={<DealerPipelinePage />} />
              <Route path="data-management" element={<LogisticsDataManagementPage />} />
              <Route path="mapping-data" element={<LogisticsMappingDataPage />} />
              <Route path="residential-consultants" element={<ResidentialConsultantsPage />} />
            </Route>

            {/* Procurement & Inventory Domain */}
            <Route path="procurement" element={<DomainProtectedRoute domain="procurement"><ProcurementLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="procurement" fallbackPath="/procurement/inventory" />} />
              <Route path="inventory" element={<ProcurementInventoryPage />} />
              <Route path="purchase-orders" element={<ProcurementPurchaseOrdersPage />} />
              <Route path="production-orders" element={<ProcurementProductionOrdersPage />} />
              <Route path="equipment" element={<ProcurementEquipmentPage />} />
              <Route path="reports" element={<ProcurementReportsPage />} />
              <Route path="data-management" element={<ProcurementDataManagementPage />} />
            </Route>

            {/* DTO (Digital Transformation Office) Domain — sidebar layout */}
            <Route path="dto" element={<DomainProtectedRoute domain="dto"><DtoLayout /></DomainProtectedRoute>}>
              <Route index            element={<DtoOverview />} />
              <Route path="overview"  element={<DtoOverview />} />
              <Route path="monthly"   element={<DtoMonthly />} />
              <Route path="categories" element={<DtoCategories />} />
              <Route path="software"  element={<Navigate to="/dto/categories?tab=SOFTWARE" replace />} />
              <Route path="pipeline"  element={<DtoPipeline />} />
              <Route path="data-management" element={<DtoDataManagement />} />
              <Route path="staff-rates" element={<Navigate to="/dto/data-management?tab=staff-rates" replace />} />
              <Route path="supplier-rules" element={<Navigate to="/dto/data-management?tab=supplier-rules" replace />} />
            </Route>

            {/* Management Pack Domain — sidebar layout */}
            <Route path="mgmt-pack" element={<DomainProtectedRoute domain="mgmt-pack"><MgmtPackLayout /></DomainProtectedRoute>}>
              <Route index                  element={<MgmtPackWidgetAdmin />} />
              <Route path="admin/widgets"   element={<MgmtPackWidgetAdmin />} />
            </Route>

            {/* Admin — one area for users, menus, widget checks, migrations and
                every domain's Data Management (pages/AdminLayout.jsx). */}
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminHome />} />
              <Route path="users" element={<AdminPanel />} />
              <Route path="roles" element={<AdminPanel />} />
              <Route path="permissions" element={<AdminPanel />} />
              <Route path="screens" element={<ScreensAdmin />} />
              <Route path="menus" element={<DomainConfigPage />} />
              <Route path="widget-validator" element={<WidgetValidator />} />
              <Route path="migrations" element={<DatabaseMigrations />} />
              <Route path="data/:key" element={<AdminDataManagement />} />
              {/* Old address for Menus */}
              <Route path="domain-config" element={<Navigate to="/admin/menus" replace />} />
            </Route>
            {/* Old addresses (before 2026-10-25) */}
            <Route path="admin-panel" element={<Navigate to="/admin/users" replace />} />
            <Route path="admin-panel/:section" element={<AdminPanelRedirect />} />

            {/* 404 */}
            <Route path="*" element={<RouteNotFound />} />
          </Route>
        </Routes>
      </React.Suspense>
        </BranchProvider>
        </BusinessEntityProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column'
  }}>
    <div style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #1e3a5f',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 1s linear infinite'
    }} />
    <p style={{ marginTop: '20px', color: '#666' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export default App;
